import React, { useState, useRef, useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import URI from "urijs";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import useAppDownloadLink from "../../hooks/useAppDownloadLink";
import { Link as LinkT, withRouter } from "react-router-dom";
import { validPath } from "../../utils/strings";
import { Button } from "@speedoc/design-assets";
import AnimatedNavIcon from "./animatedNavIcon";
import { Chevron, ChevronUpBlack, PhoneBlack } from "@speedoc/design-assets/build/icons/index";
import { GlobalStore } from "../../stores/global";

const flagUri = new URI("//public.speedoc.com/design/images/speedoc.com/flags/");

const getCountryLink = countryCode =>
  countryCode.toString().toLowerCase() === "sg" ? "https://sg.speedoc.com/en" : "https://my.speedoc.com/en";

const Link = props => {
  if (!!props.to && props.to.indexOf("http") !== -1) {
    return (
      <a {...props} href={props.to}>
        {props.children}
      </a>
    );
  }
  return <LinkT {...props} />;
};

const NavigationItem = ({ item }) => {
  return (
    <div className={!item.image?.url ? "navNoImage" : ""}>
      {item.image?.url && <img alt="ico" src={item.image.url} />}
      <div>
        <p className="text">{item.text}</p>
        {item.desc && <span className="desc">{item.desc}</span>}
      </div>
    </div>
  );
};

const NavigationMenu = ({ elements, isOneColumn }) => {
  if (isOneColumn) {
    return (
      <div className="navCol expand">
        {elements.map((element, i) => (
          <Link key={i} className={`element ${!element.desc ? " normal" : ""}`} to={validPath(element.path)}>
            <NavigationItem item={element} />
          </Link>
        ))}
      </div>
    );
  }

  return (
    <div className="navRow">
      <div>
        {elements.slice(0, Math.floor((elements.length + 1) / 2)).map((element, i) => (
          <Link
            target={element.target}
            key={i}
            className={`element ${!element.desc ? " normal" : ""}`}
            to={validPath(element.path)}>
            <NavigationItem item={element} />
          </Link>
        ))}
      </div>
      <div className="expand">
        {elements.slice(Math.floor((elements.length + 1) / 2)).map((element, i) => (
          <Link key={i} className={`element ${!element.desc ? " normal" : ""}`} to={validPath(element.path)}>
            <NavigationItem item={element} />
          </Link>
        ))}
      </div>
    </div>
  );
};

const NavigationDesktop = ({ content }) => {
  const countryCode = GlobalStore.useState(s => s.currentCountryCode);
  const countries = GlobalStore.useState(c => c.countries);
  const [menuIsOpen, setMenuIsOpen] = useState();
  const [cl, setClearTimeout] = useState(null);
  const closeTopBanner = GlobalStore.useState(s => s.closeTopBanner);

  const handleOpenMenu = index => {
    clearTimeout(cl);
    setMenuIsOpen(index);
  };

  const handleCloseMenu = () => {
    setClearTimeout(
      setTimeout(() => {
        setMenuIsOpen(undefined);
      }, 100)
    );
  };

  return (
    <div
      id="navigationDesktop"
      className={`navigationDesktopV4 ${closeTopBanner ? "hide" : ""}`}
      onMouseLeave={() => handleCloseMenu()}>
      <div className="logo" onMouseEnter={e => handleCloseMenu()}>
        <Link to={getCountryLink(countryCode)}>
          <img
            alt={content.logo && content.logo.alternativeText}
            src={"https://public.speedoc.com/strapi/logo-mobile.png?455486908.6"}
          />
        </Link>
      </div>

      <div className="navigationBar">
        {content.navigationSection.map((section, index) => (
          <div key={index} className={`link ${section.title === "Help" ? "help" : ""}`}>
            {section.elements && section.elements.length > 0 ? (
              <div className="section" onMouseEnter={() => handleOpenMenu(index)}>
                <div className="parentMenu title">
                  {section.title}
                  <span className={`chevron ${menuIsOpen === index ? "rotate" : "initial"}`}>
                    <ChevronUpBlack />
                  </span>
                </div>
                {menuIsOpen === index && (
                  <div className="submenu" onMouseLeave={() => handleCloseMenu()}>
                    <div className="submenuWrapper">
                      <NavigationMenu elements={section.elements} isOneColumn={section.isOneColumn} />
                      {section.elements
                        .filter(e => !!e.descLong)
                        .map((element, i) => (
                          <div className="descLong" key={"descLong_" + i}>
                            <p className="text">{element.text}</p>
                            <div dangerouslySetInnerHTML={{ __html: element.descLong }} />
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                {section.path ? (
                  <Link onMouseEnter={e => handleCloseMenu()} to={validPath(section.path)}>
                    <div className="parentMenu title">{section.title || section.text}</div>
                  </Link>
                ) : (
                  <span className="parentMenu title">{section.title || section.text}</span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="language" onMouseEnter={() => handleOpenMenu(-1)}>
        <div className="languageMenu">
          <img
            className="flag"
            src={flagUri
              .segment(-1, countryCode === "MY" ? "my" : "sg")
              .suffix("svg")
              .toString()}
            alt={`country flag for ${countryCode === "MY" ? "Malaysia" : "Singapore"}`}
          />
          <span className={`chevron ${menuIsOpen === -1 ? "rotate" : "initial"}`}>
            <Chevron />
          </span>
        </div>

        {menuIsOpen === -1 && (
          <div className="navigationBar">
            <div className="submenu" onMouseLeave={() => handleCloseMenu()}>
              <div className="submenuWrapper">
                <div className="navCol">
                  {countries.map(({ twoLetterCode, name }) => (
                    <Link
                      to={getCountryLink(twoLetterCode)}
                      className={`country ${countryCode === twoLetterCode ? "selected" : ""}`}
                      key={`country-${twoLetterCode}`}>
                      <img
                        src={flagUri
                          .segment(-1, twoLetterCode === "MY" ? "my" : "sg")
                          .suffix("svg")
                          .toString()}
                        alt={`country flag for ${name}`}
                      />
                      <span>{name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const NavigationMobile = ({ content }) => {
  const countryCode = GlobalStore.useState(s => s.currentCountryCode);
  const countries = GlobalStore.useState(c => c.countries);
  const [navIsShowing, setNavIsShowing] = useState(false);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [isHeaderFixed, setIsHeaderFixed] = useState(false);
  const [animationStarts, setAnimationStarts] = useState(false);
  const [navIsClosing, setNavIsClosing] = useState(false);
  const [fadeNav, setFadeNav] = useState(false);
  const [fadeText, setFadeText] = useState(false);
  const [buttonIsClickable, setButtonIsClickable] = useState(true);
  const [elementToDisplay, setElementToDisplay] = useState(true);
  const [linkHasBeenClicked, setLinkHasBeenClicked] = useState(false);
  const [menuLanguage, toggleLanguage] = useState("hide");
  const [deviceHeight, setDeviceHeight] = useState("");
  const [subMenuClicked, setSubmenuClick] = useState(false);
  const downloadLink = useAppDownloadLink();

  const component = useRef({});
  const cardHeight = index => component?.current?.children[index].querySelector(".elements").offsetHeight;
  const elHeight = document.documentElement.clientHeight;
  const closeTopBanner = GlobalStore.useState(s => s.closeTopBanner);

  useEffect(() => {
    const listenScrollEvent = e => {
      window.scrollY > 80 ? setIsHeaderFixed(true) : setIsHeaderFixed(false);
    };
    window.addEventListener("scroll", listenScrollEvent);
    return () => {
      window.removeEventListener("scroll", listenScrollEvent);
    };
  }, [isHeaderFixed]);

  useEffect(() => {
    if (elHeight >= 650 && elHeight < 700) {
      setDeviceHeight("medium");
    } else if (elHeight >= 700 && elHeight < 740) {
      setDeviceHeight("big");
    } else if (elHeight < 650 && elHeight >= 635) {
      setDeviceHeight("small");
    } else if (elHeight < 635) {
      setDeviceHeight("x-small");
    }
  }, [elHeight]);

  const triggerSectionAnimation = n => {
    for (let i = 0; i <= n; i++) {
      (function (ind) {
        setTimeout(function () {
          setElementToDisplay(ind);
        }, 100 * ind);
      })(i);
    }
  };

  const handleToggleMenu = (index, length) => {
    if (menuIsOpen === index) {
      setMenuIsOpen(undefined);
      setElementToDisplay(undefined);
    } else {
      setMenuIsOpen(index);
      triggerSectionAnimation(length);
    }
  };

  const toggleNav = (linkClicked, submenuClicked = false) => {
    if (linkClicked) setLinkHasBeenClicked(true);
    if (navIsShowing === false && buttonIsClickable) {
      setButtonIsClickable(false);
      setNavIsClosing(false);
      setNavIsShowing(true);
      setAnimationStarts(true);
    } else if (buttonIsClickable) {
      setButtonIsClickable(false);
      setMenuIsOpen(undefined);
      setElementToDisplay(undefined);
      setNavIsClosing(true);
      setAnimationStarts(true);
      setSubmenuClick(submenuClicked);
    }
  };

  useLockBodyScroll(navIsShowing, window.scrollY, subMenuClicked);

  useEffect(() => {
    const fadeInNav = () => {
      return new Promise(resolve => {
        setTimeout(() => {
          setFadeNav(!navIsClosing);
          resolve();
        }, 200);
      });
    };

    const restart = () => {
      return new Promise(resolve => {
        setTimeout(() => {
          navIsClosing === true && setNavIsShowing(false);
          resolve();
        }, 200);
      });
    };

    const fadeInText = () => {
      return new Promise(resolve => {
        setTimeout(() => {
          setFadeText(!navIsClosing);
          resolve();
        }, 100);
      });
    };

    const resetButton = () => {
      return new Promise(resolve => {
        setTimeout(() => {
          setButtonIsClickable(true);
          resolve();
        }, 50);
      });
    };

    const startAnimation = async () => {
      navIsClosing === true
        ? setTimeout(async () => {
            await fadeInText();
            await fadeInNav();
            await restart();
            await resetButton();
          }, 100)
        : setTimeout(async () => {
            await fadeInNav();
            await fadeInText();
            await restart();
            await resetButton();
          }, 100);
      setAnimationStarts(false);
    };

    animationStarts === true && startAnimation();
    setSubmenuClick(false);
  }, [animationStarts, navIsClosing]);

  return (
    <div id="navigationMobile" className={`navigationMobileV4 ${closeTopBanner ? "hide" : ""}`}>
      <div className={isHeaderFixed && !navIsShowing ? "top headerScrolled" : "top"}>
        <div className="logo">
          <Link to={getCountryLink(countryCode)}>
            <img
              alt={content.logo && content.logo.alternativeText}
              src={content.logo?.urlMobile || "https://public.speedoc.com/strapi/logo-mobile.png?455486908.6"}
            />
          </Link>
        </div>
        <div className="icons">
          <div className="menuIcon">
            <span type="button" onClick={e => toggleNav()}>
              <AnimatedNavIcon
                isClickable={buttonIsClickable}
                setLinkHasBeenClicked={setLinkHasBeenClicked}
                linkHasBeenClicked={linkHasBeenClicked}
              />
            </span>
          </div>
        </div>
      </div>
      {navIsShowing && (
        <div className={`navigation ${fadeNav ? "show" : "hide"}`}>
          <div className="mid" ref={component}>
            {content.navigationSection.map((section, index) => (
              <div key={index}>
                {section.elements && section.elements.length > 0 ? (
                  <div
                    className={`section ${fadeText ? "show" : "hide"}`}
                    onClick={() => handleToggleMenu(index, section.elements.length)}>
                    <div className="title">
                      <span>{section.title}</span>
                      <span className={`chevron ${menuIsOpen !== index ? "rotate" : "initial"}`}>
                        <ChevronUpBlack />
                      </span>
                    </div>

                    <div
                      style={{ height: menuIsOpen !== undefined && menuIsOpen === index ? cardHeight(index) : null }}
                      className="submenu">
                      <div className="elements">
                        {section.elements.map((element, i) => (
                          <Link
                            key={i}
                            target={element.target}
                            className={`element ${menuIsOpen === index && elementToDisplay >= i ? "slideIn" : ""} ${
                              !element.desc ? " normal" : ""
                            }`}
                            onClick={() => toggleNav(true, true)}
                            to={validPath(element.path)}>
                            <NavigationItem item={element} />
                          </Link>
                        ))}
                        {section.elements
                          .filter(e => !!e.descLong)
                          .map((element, i) => (
                            <div className="descLong" key={"descLong_" + i}>
                              <p className="text">{element.text}</p>
                              <div dangerouslySetInnerHTML={{ __html: element.descLong }} />
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    {section.path ? (
                      <div className={`section ${fadeText ? "show" : "hide"}`}>
                        <Link className="title" onClick={() => toggleNav(true)} to={validPath(section.path)}>
                          <span>{section.title || section.text}</span>
                        </Link>
                      </div>
                    ) : (
                      <div className="title">
                        <span>{section.title || section.text}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div className={`bottom ${fadeText ? "show" : ""}`}>
              <Button className="btnDownload">
                <a className="button secondary sm" href={downloadLink}>
                  <span>{"Download the App"}</span>
                </a>
              </Button>
              <a href={`tel:${content.phoneNumber}`} className={`linkSecondary btnCall sm ${fadeText ? "show" : ""}`}>
                <span>{"24 Hour Hotline"}</span>
                <div className="hotline">
                  <PhoneBlack />
                  <span>{content.phoneNumber}</span>
                </div>
              </a>
            </div>
            <div className={`language ${deviceHeight}`} onClick={() => {}}>
              <div className="languageMenu">
                <div className="countries">
                  <ul className={`dropdownCountries ${menuLanguage}`}>
                    {countries.map(({ twoLetterCode, name }) => (
                      <li key={`country-${twoLetterCode}`}>
                        <Link
                          type="button"
                          to={getCountryLink(twoLetterCode)}
                          className={`country ${countryCode === twoLetterCode ? "selected" : ""}`}>
                          <img
                            src={flagUri
                              .segment(-1, twoLetterCode === "MY" ? "malaysia" : "singapore")
                              .suffix("png")
                              .toString()}
                            alt={`country flag for ${name}`}
                          />
                          <span>{name}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <div
                    className="selectedCountry languageMenu"
                    onClick={() => toggleLanguage(menuLanguage === "hide" ? "show" : "hide")}>
                    <img
                      className="flag"
                      src={flagUri
                        .segment(-1, countryCode === "MY" ? "malaysia" : "singapore")
                        .suffix("png")
                        .toString()}
                      alt={`country flag for ${countryCode === "MY" ? "Malaysia" : "Singapore"}`}
                    />
                    <span>{countryCode === "MY" ? "Malaysia" : "Singapore"}</span>
                    <span className={menuLanguage === "hide" ? "chevron initial" : "chevron rotate"}>
                      <ChevronUpBlack />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Navigation = ({ ...props }) => {
  const [currentLocation, setCurrentLocation] = useState(props.location.pathname);
  const structure = GlobalStore.useState(c => c.structure);
  const [isOnLoad, setIsOnLoad] = useState(true);
  let content = structure?.navigations?.length && structure.navigations[0];
  const closeTopBanner = GlobalStore.useState(s => s.closeTopBanner);

  useEffect(() => {
    const newLocation = props.location.pathname;
    if (currentLocation !== newLocation) {
      window.scrollTo(0, 0);
      setCurrentLocation(currentLocation);
    } else if (props.location.hash) {
      let element;
      try {
        element = document.querySelector(props.location.hash);
        const topPoint = element.getBoundingClientRect().top + window.scrollY - 100;
        window.scrollTo({ behavior: "smooth", left: 0, top: topPoint });
      } catch {
        return;
      }
    }
  }, [props.location, currentLocation, setCurrentLocation]);

  useEffect(() => {
    const listenScrollEvent = () => {
      window.scrollY > 0 ? setIsOnLoad(false) : setIsOnLoad(true);
    };
    window.addEventListener("scroll", listenScrollEvent);
    return () => {
      window.removeEventListener("scroll", listenScrollEvent);
    };
  }, [isOnLoad]);

  const isMobile = useMediaQuery({ query: "(max-width: 1100px)" });

  if (content === undefined) return null;

  return (
    <>
      <div
        className={`navigationBottom ${isOnLoad ? "" : `shadow ${closeTopBanner ? "hide" : ""}`} ${
          closeTopBanner ? "hideBanner" : ""
        }`}
      />
      {isMobile ? (
        <NavigationMobile content={content} />
      ) : (
        <div className="centralContainer">
          <NavigationDesktop content={content} />
        </div>
      )}
    </>
  );
};

export default withRouter(Navigation);
